import {
  ChangelogAllocationItem,
  ChangelogItem,
  ChangelogTableFieldChange,
  ChangelogTableItem,
} from "@src/types/resource_plan_types";
import {
  AllocationRowData,
  WorkloadTableRowData,
} from "@src/types/role_request_types";
import dayjs, { Dayjs } from "dayjs";
import _ from "lodash";

import { ALLOCATION_FIELD } from "./changelog_constants";

/**
 * Maps all data to a data format for the changelog table
 *
 * @param changelogItems changelog items coming from backend
 * @returns the mapped data
 */
export function mapChangelogDataToTableData(
  changelogItems: ChangelogItem[]
): ChangelogTableItem[] {
  if (!changelogItems) return [];

  return changelogItems.map((item) => {
    const isAllocationField: boolean =
      item.fieldChange.fieldName === ALLOCATION_FIELD;

    const fieldChange: ChangelogTableFieldChange = {
      ...item.fieldChange,
      oldValue: isAllocationField ? "" : (item.fieldChange.oldValue as string),
      newValue: isAllocationField ? "" : (item.fieldChange.newValue as string),
      allocations: isAllocationField
        ? buildAllocatonData(
            item.fieldChange.oldValue,
            item.fieldChange.newValue
          )
        : undefined,
    };

    return {
      projectRoleRequestId: item.projectRoleRequestId,
      fieldChange,
    };
  });
}

/**
 * Maps the allocation data to a data format for the details table.
 *
 * @param oldValue old allocation value coming from backend
 * @param newValue new allocation value coming from backend
 * @returns row data for the details table
 */
function buildAllocatonData(
  oldValue: string | ChangelogAllocationItem[],
  newValue: string | ChangelogAllocationItem[]
): WorkloadTableRowData[] {
  const allocationMap: Map<string, AllocationRowData> =
    mapAllocationForChanglogTableToMap(oldValue, newValue);

  return [
    {
      label: "Old Value",
      allocations: allocationMap,
    },
    {
      label: "New Value",
      allocations: allocationMap,
    },
  ];
}

/**
 * Maps the allocation data from the backend to a map that can be used in the details table
 *
 * @param oldValue old allocation value coming from backend
 * @param newValue new allocation value coming from backend
 * @returns a map that can be used for the row data in the details table
 */
function mapAllocationForChanglogTableToMap(
  oldValue: string | ChangelogAllocationItem[],
  newValue: string | ChangelogAllocationItem[]
): Map<string, AllocationRowData> {
  if (!Array.isArray(newValue) || !newValue.length) {
    return new Map();
  }

  if (!Array.isArray(oldValue)) {
    oldValue = [];
  }

  const allocationMap = new Map<string, AllocationRowData>();

  // convert lists to arrays to find the values more easy
  const oldValueMap = new Map(
    oldValue.map((value) => [dayjs(value.date).format("M-YYYY"), value])
  );
  const newValueMap = new Map(
    newValue.map((value) => [dayjs(value.date).format("M-YYYY"), value])
  );

  const { firstDate, lastDate } = calculateFirstAndLastDate(oldValue, newValue);

  // builds a map out of the allocations.
  // loops through the allocations beginning with the first date until last date
  let currentValue: Dayjs = dayjs(firstDate);

  while (dayjs(currentValue).isSameOrBefore(lastDate)) {
    const key: string = currentValue.format("M-YYYY");

    const allocationDetails: AllocationRowData = {
      percentage: oldValueMap.get(key)?.allocationPercentage || 0,
      updatedPercentage: newValueMap.get(key)?.allocationPercentage || 0,
    };
    allocationMap.set(key, allocationDetails);

    currentValue = dayjs(currentValue).add(1, "month");
  }

  return allocationMap;
}

/**
 * Calculates the first and latest date of all allocations.
 * New allocations will always be given because a requests allocation cannot be changed to empty.
 * If oldValue is empty, the first and last date value of the new allocation is used.
 * Otherwise the function calculations the first and the last date of old and new allocation combined.
 *
 * @param oldValue old allocation value coming from backend
 * @param newValue new allocation value coming from backend
 * @returns firstDate and lastDate of all allocations
 */
function calculateFirstAndLastDate(
  oldValue: ChangelogAllocationItem[],
  newValue: ChangelogAllocationItem[]
): { firstDate: Date; lastDate: Date } {
  const firstNewValueDate: Date = dayjs(newValue[0].date).toDate();
  const lastNewValueDate: Date = dayjs(_.last(newValue).date).toDate();

  if (!oldValue[0]) {
    return {
      firstDate: firstNewValueDate,
      lastDate: lastNewValueDate,
    };
  }

  const firstOldValueDayjs: Dayjs = dayjs(oldValue[0].date);

  const firstDate: Date = firstOldValueDayjs.isBefore(firstNewValueDate)
    ? firstOldValueDayjs.toDate()
    : firstNewValueDate;

  const lastOldValueDayjs: Dayjs = dayjs(_.last(oldValue).date);

  const lastDate: Date = lastOldValueDayjs.isAfter(lastNewValueDate)
    ? lastOldValueDayjs.toDate()
    : lastNewValueDate;

  return { firstDate, lastDate };
}
